import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Search, Users as UsersIcon } from "lucide-react";
import { api, errMsg, fmtDate, ghs } from "../../lib/api";
import { EmptyState, PageHeader, StatusBadge } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";
import { Table, Td } from "./kit";

export function Users() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: { q } }).then((r) => setD(r.data)).catch(() => setD({ items: [], total: 0 })), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Users" text={d ? `${d.total} account(s)` : ""}>
        <label className="relative sm:w-72">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search phone, name, email" className="input-rx pl-10" data-testid="admin-user-search" />
        </label>
      </PageHeader>
      {!d ? <FullLoader /> : d.items.length === 0 ? <EmptyState icon={UsersIcon} title="No users found" /> : (
        <Table head={["Name", "Phone", "Role", "Joined", "Status", ""]} testId="admin-users-table">
          {d.items.map((u) => (
            <tr key={u.id} data-testid={`admin-user-row-${u.phone}`}>
              <Td className="font-medium text-white">{u.full_name}</Td>
              <Td className="font-mono-rx text-slate-300">{u.phone}</Td>
              <Td className="capitalize text-slate-400">{u.role}</Td>
              <Td className="text-slate-400">{fmtDate(u.created_at)}</Td>
              <Td><StatusBadge status={u.is_active ? "active" : "disabled"} label={u.is_active ? "Active" : "Disabled"} /></Td>
              <Td className="text-right"><Link to={`/admin/users/${u.id}`} className="text-xs font-semibold text-emerald-300" data-testid={`admin-user-view-${u.phone}`}>View</Link></Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function UserDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [link, setLink] = useState("");
  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => setD(r.data)).catch(() => setD(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try { await api.patch(`/admin/users/${id}/status`, { is_active: !d.user.is_active }); toast.success("Account updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const genLink = async () => {
    try { const { data } = await api.post(`/admin/users/${id}/reset-link`); setLink(`${window.location.origin}${data.path}`); } catch (e) { toast.error(errMsg(e)); }
  };

  if (d === false) return <EmptyState title="User not found" />;
  if (!d) return <FullLoader />;
  const u = d.user;
  return (
    <div className="space-y-8">
      <Link to="/admin/users" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Users</Link>
      <div className="card-rx flex flex-col gap-6 p-7 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="admin-user-name">{u.full_name}</h1>
          <p className="mt-1 font-mono-rx text-sm text-slate-400">{u.phone} {u.email && `· ${u.email}`}</p>
          <p className="mt-3 text-xs text-slate-500">Created {fmtDate(u.created_at, true)} · Last login {fmtDate(u.last_login_at, true)} · Role {u.role}</p>
          {d.open_reset_requests > 0 && <p className="mt-3 text-xs font-semibold text-amber-300">Has an open password reset request</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={u.is_active ? "active" : "disabled"} label={u.is_active ? "Active" : "Disabled"} testId="admin-user-status" />
          {u.role !== "admin" && <button onClick={toggle} className="btn-ghost py-2" data-testid="admin-user-toggle-btn">{u.is_active ? "Disable account" : "Enable account"}</button>}
          <button onClick={genLink} className="btn-ghost py-2" data-testid="admin-user-reset-link-btn">Generate reset link</button>
        </div>
      </div>
      {link && (
        <div className="card-rx flex flex-wrap items-center gap-3 p-4 text-sm" data-testid="admin-reset-link">
          <span className="flex-1 break-all font-mono-rx text-xs text-emerald-200">{link}</span>
          <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} className="btn-ghost py-2"><Copy size={14} /> Copy</button>
          <p className="w-full text-xs text-slate-500">Valid for 60 minutes. Send it to the customer through a trusted channel.</p>
        </div>
      )}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-white">Purchases ({d.purchases.length})</h2>
        {d.purchases.length === 0 ? <p className="text-sm text-slate-500">No purchases.</p> : (
          <Table head={["Item", "Type", "Amount", "Reference", "Date"]} testId="admin-user-purchases">
            {d.purchases.map((p) => (<tr key={p.id}><Td className="text-white">{p.item_name}</Td><Td className="text-slate-400">{p.item_type}</Td><Td className="font-mono-rx">{ghs(p.amount_minor)}</Td><Td className="font-mono-rx text-slate-400">{p.transaction_ref}</Td><Td className="text-slate-400">{fmtDate(p.created_at, true)}</Td></tr>))}
          </Table>
        )}
      </section>
      <section>
        <h2 className="mb-4 text-lg font-semibold text-white">Transactions ({d.transactions.length})</h2>
        {d.transactions.length === 0 ? <p className="text-sm text-slate-500">No transactions.</p> : (
          <Table head={["Reference", "Item", "Amount", "Date", "Status"]} testId="admin-user-transactions">
            {d.transactions.map((t) => (<tr key={t.reference}><Td className="font-mono-rx text-slate-300">{t.reference}</Td><Td className="text-white">{t.item_name}</Td><Td className="font-mono-rx">{ghs(t.amount_minor)}</Td><Td className="text-slate-400">{fmtDate(t.created_at, true)}</Td><Td><StatusBadge status={t.status} /></Td></tr>))}
          </Table>
        )}
      </section>
    </div>
  );
}
